<script lang="ts">
    import type {Rec} from 'typings/portaro.be.types';
    import {FileViewForm} from 'shared/constants/portaro.constants';
    import {KpCoverPresenter} from './kp-cover.presenter';
    import {getInjector, getLocalization} from 'core/svelte-context/context';
    import {exists, isFunction} from 'shared/utils/custom-utils';
    import ModalConversationService from '../../../modals/modal-conversation.service';
    import CoverImage from './CoverImage.svelte';
    import KpIconButton from 'shared/ui-widgets/button/KpIconButton.svelte';

    export let record: Rec = null;
    export let href: string = null;
    export let fillTo: 'width' | 'height' = null;
    export let target: 'cover' | 'record' = 'record';
    export let canManageCover = false;
    export let mainRecordDetailCover = false;

    const localize = getLocalization();
    const presenter = getInjector().getByToken<KpCoverPresenter>(KpCoverPresenter.presenterName);
    const modalConversationService = getInjector().tryGetByClass(ModalConversationService);

    const fillWidth = fillTo === 'width';
    const fillHeight = fillTo === 'height';
    const altText = generateAltText();
    let imgSrc = presenter.getCoverPath(record);
    $: canChangeCover = presenter.isUserLoggedIn && canManageCover;

    let enableZoomCursor = false;
    let linkHref: string = null;
    let onClickFunction: () => void = null;

    if (exists(href)) {
        linkHref = href;
    } else if (exists(record)) {
        if (target === 'cover') {
            onClickFunction = () => {
                presenter.openMediaViewer(record)
            };
            enableZoomCursor = true;
        } else if (target === 'record') {
            linkHref = presenter.getRecordPath(record);
        }
    }

    function generateAltText() {
        if (target === 'cover') {
            return `${localize(/* @kp-localization record.Cover */ 'record.Cover')}: ${record.name} (${localize(/* @kp-localization record.ClickToShowCover */ 'record.ClickToShowCover')})`;
        } else {
            return `${localize(/* @kp-localization record.Cover */ 'record.Cover')}: ${record.name} (${localize(/* @kp-localization record.ClickToShowRecord */ 'record.ClickToShowRecord')})`;
        }
    }

    const handleSelectNewCoverClick = async () => {
        const selectedFiles = await modalConversationService.filesSelection({
            parentDirectories: [record.directoryId],
            fileViewForms: [FileViewForm.IMAGE],
            multiselect: false
        });

        if (!exists(selectedFiles) || selectedFiles.length === 0) {
            return;
        }

        const newCoverFile = selectedFiles[0];
        const success = await presenter.setFileAsPrimaryCover(record, newCoverFile);

        if (!success) {
            return;
        }

        // URL parameter coverFile is ignored on backend - it is only for IMG element reactivity
        imgSrc = `${presenter.getCoverPath(record)}?coverFile=${newCoverFile.id}`;
    }
</script>

<div class="kp-cover cover-container" class:cover-main={mainRecordDetailCover}>
    {#if exists(linkHref)}
        <a class="cover-link"
           href={linkHref}
           class:zoom-in-cursor={enableZoomCursor}>

            <CoverImage {fillWidth} {fillHeight} {imgSrc} {altText}/>
        </a>
    {:else if isFunction(onClickFunction)}
        <!-- svelte-ignore a11y-invalid-attribute allow javascript:void(0) in href -->
        <a class="cover-link"
           href="javascript:void(0)"
           on:click={onClickFunction}
           class:zoom-in-cursor={enableZoomCursor}>

            <CoverImage {fillWidth} {fillHeight} {imgSrc} {altText}/>
        </a>
    {:else}
        <CoverImage {fillWidth} {fillHeight} {imgSrc} {altText}/>
    {/if}

    {#if mainRecordDetailCover && canChangeCover}
        <div class="select-cover-button-container">
            <KpIconButton icon="add-image"
                          buttonSize="md"
                          tooltipLabel="Vybrat novou obálku"
                          on:click={handleSelectNewCoverClick}/>
        </div>
    {/if}
</div>

<style lang="less">
    @import (reference) "styles/portaro.variables.less";
    @import (reference) "styles/portaro.themes.less";

    .kp-cover {
        isolation: isolate;
        width: 100%;
        height: 100%;
        position: relative;

        &:hover,
        &:focus-visible,
        &:focus-within {
            .select-cover-button-container {
                opacity: 1;
                transform: translateY(0);
            }
        }

        .select-cover-button-container {
            z-index: 1;
            position: absolute;
            top: @spacing-sm;
            right: @spacing-sm;
            transform: translateY(@spacing-xs);
            opacity: 0;
            transition: opacity 0.2s ease-in-out, transform 0.2s ease-in-out;
        }

        .zoom-in-cursor {
            cursor: zoom-in;
        }
    }

    :global {
        .kp-cover {
            &.cover-main img {
                border-radius: @border-radius-large;
            }
        }
    }
</style>